/*
 * @Author: AI Assistant
 * @Date: 2024-12-30 16:00:00
 * @LastEditors: AI Assistant
 * @LastEditTime: 2024-12-30 16:00:00
 * @FilePath: /RetailApp/retail-global-h5/src/api/cps.ts
 * @Description: CPS订单查询相关API接口
 */

import nextWork from '../../../utils/net';
import { DOMAIN_SG_NRAPI, DOMAIN_EU_NRAPI } from '../../url';

// CPS订单验证接口请求参数
export interface CPSVerifyRequest {
    saOrderId: string; // 原始SA单号，字符串类型，必传
    phoneLast4: string; // 手机号后四位（纯数字），字符串类型，必传
    idcRegion: string; // 机房区域，字符串类型，必传
    timeStamp: string; // 链接签发时间戳（毫秒），字符串类型，必传
    nonce: string; // 随机串，字符串类型，必传
    sign: string; // 签名，字符串类型，必传
}

// 运单商品项
export interface WaybillItem {
    waybillId: string; // 运单ID
    goodsName: string; // 商品名称
    orderItemId: string; // 订单项ID
    imgUrl: string; // 商品图片URL
    goodsCount: number; // 商品数量
    itemDeliveryId: string; // 配送项ID
}

// 运单信息
export interface WaybillInfo {
    address: string; // 配送地址
    orderStatusDesc: string; // 订单状态描述
    orderStatus: OrderStatus; // 订单状态（使用枚举）
    saOrderId: string; // SA订单ID
    waybillItem: WaybillItem[]; // 运单商品列表
}

export interface ExtendedWaybillInfo extends WaybillInfo {
    phoneLast4: string; // 手机号后4位
}

// CPS订单验证接口响应
export interface CPSVerifyResponse {
    code: number;
    message: string;
    data: {
        waybillList: WaybillInfo[];
    };
}

export interface CPSProduct {
    image: string;
    name: string;
    quantity: number;
}

export interface CPSOrder {
    orderId: string;
    status: string;
    logisticsStatus: string;
    products?: CPSProduct[];
    logisticsInfo?: CPSLogisticsInfo;
    createTime: string;
    address: string; // 配送地址
    phoneLast4: string; // 手机号后4位
    orderStatusDesc?: string; // 订单状态描述（实际接口返回）
    orderStatus?: OrderStatus; // 订单状态（使用枚举）
    waybillItem?: WaybillItem[]; // 运单商品列表（实际接口返回）
}

export interface CPSLogisticsInfo {
    trackList?: LogisticsTrackNode[];
    trackingNumber?: string;
}

// 物流查询请求参数
export interface CPSLogisticsQueryRequest {
    saOrderId: string; // 原始SA单号
    waybillId: string; // OD单号
    orderItemId: string; // 商品itemId
    itemDeliveryId: string; // 商品发货单号
    phoneLast4: string; // 手机号后四位（纯数字）
    idcRegion: string; // 机房区域
    timeStamp: number; // 链接签发时间戳（毫秒）
    nonce: string; // 与verify同步
    sign: string; // 签名
}

// 物流追踪扩展信息
export interface LogisticsExtendInfo {
    keyName: string;
    keyValue?: string;
    logInfos?: Array<{
        logName: string;
        logValue: string;
    }>;
}

// 物流追踪节点
export interface LogisticsTrackNode {
    node: number; // 节点代码
    extendInfos?: LogisticsExtendInfo[]; // 扩展信息
    level: number; // 级别
    operatorTime?: string; // 操作时间（格式如 "2025-11-26 02:39:03"）
    action: string; // 动作代码
    operator?: string; // 操作人
}

// 物流查询响应
export interface CPSLogisticsQueryResponseData {
    code: number;
    message: string;
    data: {
        trackList: LogisticsTrackNode[];
    };
}

// API接口URL配置
export const CPS_API_URLS = {
    VERIFY: '/api/nstrade/cps/verify',
    QUERY_LOGISTICS: '/api/nstrade/cps/queryLogistics'
};

// CPS订单验证接口
export const verifyCPSOrder = (request: CPSVerifyRequest[]): Promise<CPSVerifyResponse> => {
    const env = process.env.BUILD_APPDOMAIN || 'development';
    const isDevelopment = process.env.NODE_ENV === 'development';

    // 根据idcRegion参数选择对应的域名（取第一个元素的idcRegion）
    let domain = '';
    if (request.length > 0) {
        const firstRequest = request[0];
        if (firstRequest.idcRegion === 'sg') {
            domain = DOMAIN_SG_NRAPI[env] || DOMAIN_SG_NRAPI['development'];
        } else if (firstRequest.idcRegion === 'eu') {
            domain = DOMAIN_EU_NRAPI[env] || DOMAIN_EU_NRAPI['development'];
        } else {
            // 默认使用新加坡域名
            domain = DOMAIN_SG_NRAPI[env] || DOMAIN_SG_NRAPI['development'];
        }
    } else {
        // 默认使用新加坡域名
        domain = DOMAIN_SG_NRAPI[env] || DOMAIN_SG_NRAPI['development'];
    }

    // 开发环境使用相对路径（通过代理），生产环境使用完整URL
    const url = isDevelopment
        ? CPS_API_URLS.VERIFY  // 开发环境：使用相对路径，通过setupProxy.js代理
        : `${domain}${CPS_API_URLS.VERIFY}`; // 生产环境：根据idcRegion使用对应的完整URL

    return nextWork.post(url, request, false) as Promise<CPSVerifyResponse>;
};

export const queryCPSLogistics = (request: CPSLogisticsQueryRequest[]): Promise<CPSLogisticsQueryResponseData> => {
    const env = process.env.BUILD_APPDOMAIN || 'development';
    const isDevelopment = process.env.NODE_ENV === 'development';

    // 根据idcRegion参数选择对应的域名（取第一个元素的idcRegion）
    let domain = '';
    if (request.length > 0) {
        const firstRequest = request[0];
        if (firstRequest.idcRegion === 'sg') {
            domain = DOMAIN_SG_NRAPI[env] || DOMAIN_SG_NRAPI['development'];
        } else if (firstRequest.idcRegion === 'eu') {
            domain = DOMAIN_EU_NRAPI[env] || DOMAIN_EU_NRAPI['development'];
        } else {
            // 默认使用新加坡域名
            domain = DOMAIN_SG_NRAPI[env] || DOMAIN_SG_NRAPI['development'];
        }
    } else {
        // 默认使用新加坡域名
        domain = DOMAIN_SG_NRAPI[env] || DOMAIN_SG_NRAPI['development'];
    }

    // 开发环境使用相对路径（通过代理），生产环境使用完整URL
    const url = isDevelopment
        ? CPS_API_URLS.QUERY_LOGISTICS  // 开发环境：使用相对路径，通过setupProxy.js代理
        : `${domain}${CPS_API_URLS.QUERY_LOGISTICS}`; // 生产环境：根据idcRegion使用对应的完整URL

    return nextWork.post(url, request, false) as Promise<CPSLogisticsQueryResponseData>;
};

/**
 * 订单状态枚举
 */
export enum OrderStatus {
    ALL_STATUS = -10, // 全部
    WAIT_FOR_PAY = 1, // 待支付
    PRE_PAID = 3, // 已预付
    PAY_SUCCESS = 4, // 已付款
    PREPARED = 6, // 已配货
    DELIVERING_SUCCESS = 7, // 已出库
    DELIVERED_SUCCESS = 8, // 已妥投
    REFUNDING = 17, // 正在退款
    CLOSED_SUCCESS = 18, // 已关闭
    CREATE_FAIL = 25, // 建单失败
    REFUND_SUCCESS = 39, // 已退款
    PARTIAL_PAID = 40 // 支付中
}

